import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { formatPrice } from "@/lib/format";
import { Coins, Loader2, Sparkles, Gift, Users, Share2, Copy, Check, RefreshCw, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { GiftCreditsDialog } from "@/components/GiftCreditsDialog";
import { VoucherRedeem } from "@/components/VoucherRedeem";
import { AddonInfoButton } from "@/components/AddonInfoButton";
import { getAddonInfo } from "@/lib/creditAddons";
import {
  customCreditUnitPriceFromPackages,
  customCreditPurchasePrice,
  customCreditEffectiveUnitPrice,
} from "@/lib/customCreditUnitPrice";
import { subscriptionCheckoutPrice } from "@/lib/subscriptionPricing";
import { DealerPackageCard } from "@/components/DealerPackageCard";
import { PersonalQuoteRequestCard } from "@/components/PersonalQuoteRequestCard";
import { SITE_CONTAINER } from "@/lib/siteLayout";

export default function Credits() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loadingPkg, setLoadingPkg] = useState(null);
  const [customCredits, setCustomCredits] = useState(100);
  const [loadingCustom, setLoadingCustom] = useState(false);
  const [copied, setCopied] = useState(false);
  /** onetime | month | year */
  const [purchaseMode, setPurchaseMode] = useState("onetime");
  const [sub, setSub] = useState(null);
  const [giftOpen, setGiftOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const loadSub = () => api.get("/credits/subscription").then((r) => setSub(r.data.subscription)).catch(() => {});

  useEffect(() => {
    api.get("/pricing-config").then((r) => setConfig(r.data)).catch(() => {});
    refreshUser().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) loadSub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const entryUnitPrice = config
    ? (config.custom_credit_unit_price ?? customCreditUnitPriceFromPackages(config.packages))
    : 10;
  const customAmount = Math.max(0, Math.floor(Number(customCredits) || 0));
  const customTotal = config ? customCreditPurchasePrice(config.packages, customAmount) : customAmount * entryUnitPrice;
  const customUnitPrice = customAmount > 0 && config
    ? customCreditEffectiveUnitPrice(config.packages, customAmount)
    : entryUnitPrice;

  const annualDiscount = config?.credit_subscription_annual_discount_percent ?? 20;
  const isSubscribe = purchaseMode === "month" || purchaseMode === "year";

  const buy = async (idx) => {
    if (!user) return navigate("/belepes");
    if (isSubscribe && sub?.dealer_pack) {
      toast.error("Kereskedői előfizetés mellett előbb mondd le azt, vagy használd a kereskedői csomagot.");
      return;
    }
    setLoadingPkg(idx);
    try {
      if (purchaseMode === "onetime") {
        const res = await api.post("/credits/checkout", { package_index: idx, origin_url: window.location.origin });
        window.location.href = res.data.checkout_url;
      } else {
        const res = await api.post("/credits/subscribe", {
          package_index: idx,
          billing_interval: purchaseMode,
          origin_url: window.location.origin,
        });
        window.location.href = res.data.checkout_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nem sikerült elindítani a fizetést.");
      setLoadingPkg(null);
    }
  };

  const buyCustom = async () => {
    if (!user) return navigate("/belepes");
    const amount = Math.floor(Number(customCredits));
    if (!Number.isFinite(amount) || amount < 100 || amount > 100_000) {
      toast.error("100–100 000 kredit között adj meg mennyiséget.");
      return;
    }
    setLoadingCustom(true);
    try {
      const res = await api.post("/credits/checkout", {
        custom_credits: amount,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Nem sikerült elindítani a fizetést.");
      setLoadingCustom(false);
    }
  };

  const cancelSub = async () => {
    if (!window.confirm("Biztosan lemondod a kredit előfizetést? A jelenlegi periódus végéig aktív marad.")) return;
    setCancelling(true);
    try {
      await api.post("/credits/subscription/cancel");
      toast.success("Előfizetés lemondva – a jelenlegi ciklus végéig aktív");
      loadSub();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sikertelen lemondás");
    } finally {
      setCancelling(false);
    }
  };

  const copyRef = () => {
    const link = `${window.location.origin}/belepes?ref=${user?.referral_code || ""}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success("Meghívó link a vágólapon");
  };

  const rewards = config?.rewards || {};
  const oneTime = config?.addons_one_time || {};
  const weekly = config?.addons_weekly || {};

  return (
    <div className="dark min-h-screen bg-anthracite-900 text-white">
      <Navbar />
      <section className="pt-32 pb-24">
        <div className={SITE_CONTAINER}>
          <div className="text-center">
            <div className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-electric mb-3">
              <Sparkles className="w-4 h-4" strokeWidth={1.5} /> Kredit vásárlás
            </div>
            <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tighter">Töltsd fel a kredit egyenleged</h1>
            <p className="text-slate-400 mt-3 max-w-2xl mx-auto">
              Egyedi mennyiség ugyanazzal az árazással, mint a csomagok (pl. 500 kredit = ugyanannyi Ft, mint a 500-as csomag). A hirdetésfeladás egyszeri{" "}
              {config?.listing_upload ?? 20} kredit, majd napi {config?.listing_daily ?? 10} kredit amíg fent van.
            </p>
          </div>

          {user && (
            <div data-testid="credits-balance-hero" className="mt-8 glass rounded-3xl p-6 flex flex-wrap items-center justify-between gap-4 max-w-3xl mx-auto">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-electric/15 flex items-center justify-center">
                  <Coins className="w-6 h-6 text-electric" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-400">Aktuális egyenleged</div>
                  <div className="font-heading text-3xl font-light" data-testid="credits-balance-value">{user.credits ?? 0} kredit</div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="open-gift-dialog"
                  onClick={() => setGiftOpen(true)}
                  className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25 text-sm border border-emerald-400/30"
                >
                  <Gift className="w-4 h-4" /> Ajándékozás
                </button>
                <Link to="/fiok" className="px-4 py-2 rounded-full glass hover:bg-white/10 text-sm">Levonások →</Link>
              </div>
            </div>
          )}

          {/* Active subscription card */}
          {user && sub && (
            <div data-testid="active-subscription-card" className={`max-w-3xl mx-auto mt-4 glass-strong rounded-3xl p-5 flex flex-wrap items-center justify-between gap-3 border ${sub.status === "cancelling" ? "border-amber-400/30" : "border-emerald-400/30"}`}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/15 flex items-center justify-center">
                  <RefreshCw className="w-4 h-4 text-emerald-400" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-400">
                    {sub.dealer_pack
                      ? "Kereskedői előfizetés"
                      : (sub.billing_interval || "month") === "year"
                        ? "Éves kreditcsomag"
                        : "Havi kreditcsomag"}
                  </div>
                  <div data-testid="active-sub-summary" className="font-heading text-lg">
                    {(sub.monthly_credits ?? sub.credits)} kredit / hó · {formatPrice(sub.price)}
                    {(sub.billing_interval || "month") === "year" ? " / év" : " / hó"}
                  </div>
                  {sub.dealer_pack && (
                    <div className="text-xs text-slate-500 mt-1">
                      {sub.rotating_cars ?? "—"} forgó autó · {sub.garage_spots ?? "—"} garázshely
                    </div>
                  )}
                  <div className="text-xs text-slate-500">Státusz: <span data-testid="active-sub-status">{sub.status === "cancelling" ? "Lemondás alatt (a periódus végéig aktív)" : "Aktív"}</span></div>
                </div>
              </div>
              {sub.status === "active" && (
                <button
                  data-testid="cancel-subscription"
                  disabled={cancelling}
                  onClick={cancelSub}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-red-500/10 hover:bg-red-500/20 text-red-300 text-xs"
                >
                  {cancelling ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <X className="w-3.5 h-3.5" />} Lemondás
                </button>
              )}
            </div>
          )}

          {user && <VoucherRedeem onRedeemed={() => refreshUser().catch(() => {})} />}

          {/* Purchase mode */}
          <div className="mt-8 flex justify-center">
            <div data-testid="purchase-mode-toggle" className="inline-flex flex-wrap items-center justify-center gap-1 bg-white/[0.03] rounded-full p-1 border border-white/10">
              <button
                data-testid="monthly-toggle-onetime"
                onClick={() => setPurchaseMode("onetime")}
                className={`px-4 py-1.5 rounded-full text-xs transition-all ${purchaseMode === "onetime" ? "bg-electric text-white" : "text-slate-400"}`}
              >
                Egyszeri vásárlás
              </button>
              <button
                data-testid="monthly-toggle-monthly"
                onClick={() => setPurchaseMode("month")}
                className={`px-4 py-1.5 rounded-full text-xs transition-all ${purchaseMode === "month" ? "bg-electric text-white" : "text-slate-400"}`}
              >
                Havi előfizetés
              </button>
              <button
                data-testid="yearly-toggle"
                onClick={() => setPurchaseMode("year")}
                className={`px-4 py-1.5 rounded-full text-xs transition-all ${purchaseMode === "year" ? "bg-electric text-white" : "text-slate-400"}`}
              >
                Éves előfizetés (−{annualDiscount}%)
              </button>
            </div>
          </div>

          {!isSubscribe && (
            <div
              data-testid="custom-credit-purchase"
              className="mt-6 max-w-3xl mx-auto glass rounded-3xl p-6 border border-white/10"
            >
              <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Egyedi mennyiség</div>
                  <label className="text-sm text-slate-300 block mb-2" htmlFor="custom-credits-input">
                    Hány kreditet szeretnél? (min. 100 — az ár a megfelelő csomagsáv szerint számolódik)
                  </label>
                  <input
                    id="custom-credits-input"
                    data-testid="custom-credits-input"
                    type="number"
                    min={100}
                    max={100000}
                    step={1}
                    value={customCredits}
                    onChange={(e) => setCustomCredits(e.target.value)}
                    className="w-full max-w-xs bg-anthracite-900 border border-white/10 rounded-xl px-4 py-3 text-lg font-heading focus:border-electric outline-none"
                  />
                </div>
                <div className="text-center sm:text-right shrink-0">
                  <div className="text-xs text-slate-500">Fizetendő</div>
                  <div className="font-heading text-2xl" data-testid="custom-credits-total">
                    {formatPrice(customTotal)}
                  </div>
                  {customAmount >= 100 && (
                    <div className="text-xs text-slate-500 mt-1" data-testid="custom-credits-unit">
                      ≈ {customUnitPrice.toFixed(1)} Ft / kredit
                    </div>
                  )}
                </div>
              </div>
              <button
                type="button"
                data-testid="buy-custom-credits"
                onClick={buyCustom}
                disabled={loadingCustom}
                className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3 rounded-full bg-white/10 hover:bg-white/15 border border-white/15 disabled:opacity-50 text-white text-sm font-medium transition-all"
              >
                {loadingCustom && <Loader2 className="w-4 h-4 animate-spin" />}
                Egyedi mennyiség megvétele
              </button>
            </div>
          )}

          {/* Packages */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-6 items-stretch auto-rows-fr">
            {(config?.packages || []).map((p, i) => {
              const perCredit = p.price / p.credits;
              const packages = config?.packages || [];
              const minPerCredit = packages.length
                ? Math.min(...packages.map((x) => x.price / x.credits))
                : perCredit;
              const bestValue = !isSubscribe && perCredit <= minPerCredit + 0.001;
              const discountPct = entryUnitPrice > 0 && perCredit < entryUnitPrice
                ? Math.round((1 - perCredit / entryUnitPrice) * 100)
                : 0;
              const checkoutPrice = isSubscribe
                ? subscriptionCheckoutPrice(p, purchaseMode, annualDiscount)
                : p.price;
              const priceSuffix = purchaseMode === "year" ? " / év" : purchaseMode === "month" ? " / hó" : "";
              return (
                <div
                  key={i}
                  data-testid={`credit-package-${i}`}
                  className={`glass rounded-3xl p-6 flex flex-col h-full min-h-0 ${
                    bestValue || purchaseMode === "year" ? "list-boost-pulse" : "border border-white/10"
                  }`}
                >
                  <div className="min-h-[26px] mb-3">
                    {bestValue && purchaseMode === "onetime" && (
                      <span className="inline-flex px-2.5 py-1 rounded-full bg-electric/20 text-electric text-[10px] tracking-[0.2em] uppercase">
                        Legjobb ár
                      </span>
                    )}
                    {purchaseMode === "year" && (
                      <span className="inline-flex px-2.5 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-[10px] tracking-[0.2em] uppercase">
                        −{annualDiscount}% éves
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <Coins className="w-5 h-5 text-electric" strokeWidth={1.5} />
                    <span className="text-xs uppercase tracking-widest text-slate-400">Kreditcsomag</span>
                  </div>
                  <div className="font-heading text-4xl font-light" data-testid={`credit-package-credits-${i}`}>{p.credits}</div>
                  <div className="text-sm text-slate-400 mt-1 min-h-[2.75rem]">
                    kredit{isSubscribe ? " / hó" : ""}
                    {purchaseMode === "year" && (
                      <span className="block text-[11px] text-slate-500 mt-0.5">
                        évente egyszeri feltöltés: {(p.credits * 12).toLocaleString("hu-HU")} kredit
                      </span>
                    )}
                  </div>
                  <div className="flex-1 min-h-2" aria-hidden />
                  <div>
                    <div className="font-heading text-2xl" data-testid={`credit-package-price-${i}`}>
                      {formatPrice(checkoutPrice)}
                      {priceSuffix && <span className="text-xs text-slate-500">{priceSuffix}</span>}
                    </div>
                    <div className="text-xs text-slate-500 mt-1 min-h-[2.5rem] leading-relaxed">
                      {perCredit.toFixed(1)} Ft / kredit (havi csomag)
                      {purchaseMode === "onetime" && discountPct > 0 && (
                        <span className="text-emerald-400 ml-1">−{discountPct}% vs egyedi</span>
                      )}
                      {purchaseMode === "year" && (
                        <span className="text-emerald-400 ml-1">
                          12× havi díj −{annualDiscount}%
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    data-testid={`buy-credit-package-${i}`}
                    onClick={() => buy(i)}
                    disabled={loadingPkg === i}
                    className="mt-5 w-full inline-flex items-center justify-center gap-2 py-3 rounded-full bg-electric hover:bg-electric-light disabled:opacity-50 text-white text-sm font-medium transition-all shrink-0"
                  >
                    {loadingPkg === i && <Loader2 className="w-4 h-4 animate-spin" />}
                    {purchaseMode === "onetime" ? "Megveszem" : purchaseMode === "year" ? "Éves előfizetés" : "Havi előfizetés"}
                  </button>
                </div>
              );
            })}

            {config && (
              <div className="col-span-full grid grid-cols-1 lg:grid-cols-8 gap-5 items-stretch">
                <div className="lg:col-span-5 min-h-0">
                  <DealerPackageCard config={config} user={user} activeSub={sub} />
                </div>
                <div className="lg:col-span-3 min-h-0">
                  <PersonalQuoteRequestCard user={user} />
                </div>
              </div>
            )}
          </div>

          {/* Rewards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-14">
            <div className="glass rounded-2xl p-5">
              <Gift className="w-5 h-5 text-emerald-400" strokeWidth={1.5} />
              <div className="font-heading text-lg mt-2">Regisztrációs ajándék</div>
              <div className="text-sm text-slate-400 mt-1">Minden új eladó <b className="text-emerald-400">{rewards.signup ?? 20} kredit</b>-et kap ajándékba.</div>
            </div>
            <div className="glass rounded-2xl p-5">
              <Sparkles className="w-5 h-5 text-electric" strokeWidth={1.5} />
              <div className="font-heading text-lg mt-2">Vélemény jutalom</div>
              <div className="text-sm text-slate-400 mt-1">Bejelentkezve véleményt írsz? <b className="text-electric">5★ = {rewards.review_5star ?? 100}</b>, <b>4★ = {rewards.review_4star ?? 50}</b>, egyéb <b>{rewards.review_lower ?? 10}</b> kredit.</div>
            </div>
            <div className="glass rounded-2xl p-5">
              <Users className="w-5 h-5 text-amber-400" strokeWidth={1.5} />
              <div className="font-heading text-lg mt-2">Referral bónusz</div>
              <div className="text-sm text-slate-400 mt-1">Ha a meghívó linkeddel <b>{rewards.referral_threshold ?? 2}</b> új eladó regisztrál, <b className="text-amber-400">{rewards.referral ?? 100} kredit</b> a tiéd.</div>
            </div>
          </div>

          {/* Referral link */}
          {user && (
            <div className="glass-strong rounded-3xl p-6 mt-6 flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Share2 className="w-5 h-5 text-electric" strokeWidth={1.5} />
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-400">A meghívó linked</div>
                  <div className="font-mono text-sm text-slate-200 break-all" data-testid="referral-link">
                    {window.location.origin}/belepes?ref={user.referral_code}
                  </div>
                </div>
              </div>
              <button
                data-testid="copy-referral"
                onClick={copyRef}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-electric hover:bg-electric-light text-white text-sm"
              >
                {copied ? <><Check className="w-4 h-4" /> Másolva</> : <><Copy className="w-4 h-4" /> Link másolása</>}
              </button>
            </div>
          )}

          {/* Addon cost table */}
          <div className="mt-14">
            <h2 className="font-heading text-2xl font-light mb-4">Kiegészítők ára</h2>
            <p className="text-xs text-slate-500 mb-4">Kattints a ⓘ ikonra a pontos magyarázatért.</p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="glass rounded-2xl p-5">
                <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">Alapdíjak</div>
                <div className="space-y-2 text-sm">
                  <CreditRow addonKey="listing_upload" value={`${config?.listing_upload ?? 20} kredit`} />
                  <CreditRow addonKey="listing_daily" value={`${config?.listing_daily ?? 10} kredit / nap`} />
                </div>
              </div>
              <div className="glass rounded-2xl p-5">
                <div className="text-xs uppercase tracking-widest text-electric mb-2">Egyszeri</div>
                <div className="space-y-2 text-sm">
                  <CreditRow addonKey="viewer_360" value={`${oneTime.viewer_360 ?? 40} kredit`} />
                  <CreditRow addonKey="ai_photo_bg" value={`${oneTime.ai_photo_bg ?? 30} kredit`} />
                  <CreditRow addonKey="ai_customer_support" value={`${oneTime.ai_customer_support ?? 50} kredit`} />
                  <CreditRow
                    addonKey="facebook_crosspost"
                    value={`${oneTime.facebook_crosspost ?? 150} kredit / kérés`}
                  />
                </div>
              </div>
              <div className="glass rounded-2xl p-5">
                <div className="text-xs uppercase tracking-widest text-amber-400 mb-2">Heti</div>
                <div className="space-y-2 text-sm">
                  <CreditRow addonKey="list_boost" value={`${weekly.list_boost ?? 30} kredit / hét`} />
                  <CreditRow addonKey="priority_ranking" value={`${weekly.priority_ranking ?? 50} kredit / hét`} />
                  <CreditRow addonKey="meta_ads" value={`${weekly.meta_ads ?? 500} kredit / hét`} />
                  <CreditRow addonKey="detailed_stats" value={`+${config?.detailed_stats_daily ?? 5} kredit / nap / autó (opció)`} />
                </div>
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-slate-500 mt-8">
            A fizetés a Stripe biztonságos fizetési kapuján keresztül történik.{" "}
            <Link to="/aszf" className="text-electric hover:underline">
              Díjazás, előfizetés és elállás (ÁSZF)
            </Link>
            .
          </p>
        </div>
      </section>
      <Footer />

      <GiftCreditsDialog open={giftOpen} onClose={() => setGiftOpen(false)} balance={user?.credits ?? 0} onSent={() => refreshUser().catch(() => {})} />
    </div>
  );
}

const CreditRow = ({ addonKey, value }) => {
  const info = getAddonInfo(addonKey);
  return (
    <div className="flex items-center justify-between gap-3 border-b border-white/5 py-2 last:border-0">
      <span className="text-slate-300 inline-flex items-center gap-1.5 min-w-0">
        <span className="truncate">{info?.label || addonKey}</span>
        <AddonInfoButton addonKey={addonKey} testid={`credits-info-${addonKey}`} />
      </span>
      <span className="text-slate-100 font-medium shrink-0 text-right">{value}</span>
    </div>
  );
};
